import type { CarFormState } from '@pcln/horizon-search-form'
import { CarSearchForm } from '@pcln/horizon-search-form'
import { useState } from 'react'

import { useFlightAirportNesting } from '@/components/TypeAhead/useFlightAirportNesting'
import type { GA4PageNameType } from '@/ga4types'
import useBootstrapData from '@/hooks/useBootstrapData'
import useSeti from '@/hooks/useSeti'
import logSearchFormAnalytics from '@/shared-utils/log-search-form-analytics'
import type { LOCATION_SEARCH_TYPE } from '@/types'

import { fireBundleAndSaveEvent, fireItemClickEvent } from '../ga4'
import { handleSubmitForCars } from '../SearchForm/utils'
import type { CAR_FORM_STATE_TYPE } from '../types'
import useCarFormInitialValues from './useCarFormInitialValues'

type FormType = {
  onSubmit?: (data: CAR_FORM_STATE_TYPE, pageName: GA4PageNameType) => void
  setLocation?: (city?: LOCATION_SEARCH_TYPE) => void
  pageName?: 'homepage' | 'landing'
}

export default function HorizonCarForm({
  onSubmit = handleSubmitForCars,
  setLocation,
  pageName = 'homepage',
}: Readonly<FormType>) {
  const { locale: { localeCode = 'en-US' } = {} } = useBootstrapData()
  const { initialValues, isMobile, cguid } = useCarFormInitialValues()
  const removeBackground =
    useSeti({ experimentCode: 'HP_SEARCH_FORM_HORIZON_CROSS_SELL_DESKTOP' }) === 'VARIANT'
  const defaultHotelDestinationFromPickup =
    useSeti({ experimentCode: 'HP_SEARCH_RC_DEFAULT_PKG' }) === 'VARIANT'
  const isPackageCheckboxHighlightEnabled =
    useSeti({
      experimentCode: 'PKG_MWEB_CHECKBOX_HIGHLIGHT',
      shouldFireImpression: isMobile,
    }) === 'VARIANT' && isMobile

  // Bundle & Save's "Add a flight" swaps both location inputs onto the flights typeahead. Only
  // then does WEB_MWEB_FLIGHT_SEARCH_AIRPORT_NESTING apply — passing a non-flights endpoint keeps
  // the hook from recording exposure for car-only users who never see a flight field.
  const [hasFlightBundle, setHasFlightBundle] = useState(false)
  const airportNestingEnabled = useFlightAirportNesting(hasFlightBundle ? 'flights' : 'cars')

  return (
    <CarSearchForm
      initialValues={initialValues as unknown as Partial<CarFormState>}
      isMobile={isMobile}
      autoAdvance={!isMobile}
      localeCode={localeCode}
      setLocation={setLocation}
      removeBackground={removeBackground}
      highlightBundleAndSave={isPackageCheckboxHighlightEnabled}
      defaultHotelDestinationFromPickup={defaultHotelDestinationFromPickup}
      onItemClick={() => fireItemClickEvent()}
      onBundleChange={(tripType, checked) => {
        setHasFlightBundle(tripType.includes('FLY'))
        if (checked) {
          try {
            fireBundleAndSaveEvent(tripType)
          } catch {
            // GA4TripTypeMapper may throw on unexpected trip type combinations
          }
        }
      }}
      onSubmit={(data) => {
        onSubmit(data, pageName)
        logSearchFormAnalytics({ cguid, pageName, productType: 'cars', isMobile })
      }}
      autocompleteOptions={({ typeAheadEndpoint }) => ({
        isMobile,
        airportNestingEnabled: typeAheadEndpoint === 'flights' && airportNestingEnabled,
      })}
    />
  )
}
